<script lang="ts">
export default {
  name: 'PolicySublimits',
};
</script>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { PolicySublimit } from '@/types/PolicySublimit';
import { addCommas, isNumeric, removeCommas } from '@/utilities/Number';
import { policyService } from '@/services/api';
import { compactMoney } from '@/utilities/Money';
import { Check, X, Pencil } from 'lucide-vue-next';

const emit = defineEmits(['edit', 'refresh']);

const props = defineProps<{
  coverageType: string;
  sublimits: PolicySublimit[];
  canEdit?: boolean;
}>();

const editingSublimitID = ref(undefined as number | undefined);
const editingSublimitValue = ref(undefined as number | string | undefined);
const editingSublimitSaving = ref(false);

const policyId = computed(() => props.sublimits[0].policyId);

const editingSublimitDisplayValue = computed({
  get: () => {
    return editingSublimitValue.value ? addCommas(editingSublimitValue.value) : '';
  },
  set: (val) => {
    editingSublimitValue.value = removeCommas(val)?.toString() || '';
  },
});

const editingSublimitErrors = computed(() => {
  if (!editingSublimitID.value) return undefined;
  if (!editingSublimitValue.value) return 'Value is required';
  if (!isNumeric(editingSublimitValue.value)) return 'Value must be numeric';
  return undefined;
});

function editSublimit(id: number, value: number): void {
  editingSublimitID.value = id;
  editingSublimitValue.value = value;
  emit('edit', true);
}

function cancelEditingSublimit(): void {
  editingSublimitID.value = undefined;
  editingSublimitValue.value = undefined;
  emit('edit', false);
}

async function saveSublimit(): Promise<void> {
  editingSublimitSaving.value = true;
  try {
    if (editingSublimitID.value && editingSublimitValue.value) {
      await policyService.patchSublimit(policyId.value, editingSublimitID.value, parseFloat('' + editingSublimitValue.value));
      editingSublimitID.value = undefined;
      editingSublimitValue.value = undefined;
      emit('edit', false);
      emit('refresh', policyId.value);
    }
  } catch (err) {
    console.error(err);
  } finally {
    editingSublimitSaving.value = false;
  }
}
</script>

<template>
  <div class="tw:rounded-box tw:border tw:border-base-content/15 tw:bg-base-100 tw:p-0.5">
    <table
      id="policy-sublimits"
      class="tw:table-pin-rows tw:table tw:overflow-auto tw:rounded-box tw:table-zebra">
      <thead>
        <tr>
          <td class="">Sublimit Description</td>
          <td class="tw:pr-9 tw:text-right">Limit</td>
        </tr>
      </thead>

      <tbody>
        <tr
          v-for="sublimit in sublimits"
          :key="sublimit.id"
          class="tw:group tw:hover:bg-gray-50">
          <td>{{ sublimit.name }}</td>
          <td
            class="tw:w-50"
            :data-raw="sublimit.value">
            <div
              class="tw:flex tw:flex-col tw:gap-2"
              v-if="sublimit.id === editingSublimitID">
              <div class="tw:flex tw:gap-2">
                <input
                  type="text"
                  class="tw:input tw:input-sm"
                  v-model="editingSublimitDisplayValue" />

                <button
                  class="tw:btn tw:btn-circle tw:self-center tw:btn-xs tw:btn-primary"
                  :class="{ 'tw:btn-disabled': !!editingSublimitErrors }"
                  @click="saveSublimit">
                  <Check
                    :size="14"
                    class=""
                    v-if="!editingSublimitSaving" />
                </button>

                <button
                  class="tw:btn tw:btn-circle tw:self-center tw:btn-xs"
                  @click="cancelEditingSublimit">
                  <X
                    :size="14"
                    class="tw:text-primary" />
                </button>
              </div>

              <div
                v-if="editingSublimitErrors"
                class="tw:text-xs tw:text-red-500">
                {{ editingSublimitErrors }}
              </div>
            </div>

            <div
              class="tw:flex tw:h-8 tw:items-center tw:justify-end tw:gap-2"
              v-else>
              {{ compactMoney(sublimit.value) }}

              <button
                v-if="canEdit"
                class="tw:btn tw:opacity-30 tw:btn-ghost tw:transition-opacity tw:btn-sm tw:group-hover:opacity-100"
                title="Edit sublimit"
                @click="editSublimit(sublimit.id, sublimit.value)">
                <Pencil :size="16" />
              </button>
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
